import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { acquistiApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, TrendingUp, TrendingDown, Store } from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { fmtPrezzo, fmtSoldi, fmtQta, fmtUm, fmtDataIt, fmtVariazione, coloreVariazione, ND } from '../../lib/acquisti';
import CategoriaSelect, { NESSUNA } from '../../components/acquisti/CategoriaSelect';
import { useAuth } from '../../context/AuthContext';

const Dato = ({ label, valore, testid, classe = '' }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className={`font-semibold tabular-nums ${classe}`} data-testid={testid}>{valore}</p>
  </div>
);

const AcquistiProdottoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.ruolo === 'admin' || user?.is_super_admin;
  const [dati, setDati] = useState(null);
  const [categorieCosto, setCategorieCosto] = useState([]);
  const [caricando, setCaricando] = useState(true);

  useEffect(() => {
    acquistiApi.getStato().then((s) => setCategorieCosto(s.categorie || [])).catch(() => {});
  }, []);

  const salvaCategoria = async (categoria) => {
    try {
      await acquistiApi.setCategoriaProdotto(id, categoria === NESSUNA ? null : categoria);
      toast.success('Categoria del prodotto salvata e ricordata per i prossimi import');
      setDati(await acquistiApi.getProdotto(id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Salvataggio non riuscito');
    }
  };

  useEffect(() => {
    (async () => {
      setCaricando(true);
      try {
        setDati(await acquistiApi.getProdotto(id));
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Prodotto non trovato');
      } finally {
        setCaricando(false);
      }
    })();
  }, [id]);

  const p = dati?.prodotto;
  const storico = dati?.storico || [];
  const serie = storico
    .filter((s) => s.prezzo_netto_unitario !== null && s.prezzo_netto_unitario !== undefined)
    .map((s) => ({ data: fmtDataIt(s.data).slice(0, 5), prezzo: s.prezzo_netto_unitario, completa: fmtDataIt(s.data) }));

  return (
    <div className="min-h-screen pb-24">
      <header className="glass-header sticky top-0 z-40 border-b border-border">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/acquisti')} data-testid="prodotto-back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-base font-bold leading-tight truncate" data-testid="prodotto-titolo">
              {p?.descrizione || 'Prodotto'}
            </h1>
            <p className="text-xs text-muted-foreground truncate">{p?.fornitore_nome}</p>
          </div>
        </div>
      </header>

      <main className="px-4 py-4 max-w-lg mx-auto space-y-4">
        {caricando && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Caricamento…
          </div>
        )}

        {p && (
          <>
            <Card data-testid="prodotto-riepilogo">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-end justify-between gap-3">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">Ultimo prezzo</p>
                    <p className="text-3xl font-bold tabular-nums" data-testid="prodotto-ultimo-prezzo">
                      {fmtPrezzo(p.ultimo_prezzo)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      per {fmtUm(p.um_originale, p.um_norm)} · {fmtDataIt(p.ultima_data)}
                    </p>
                  </div>
                  {p.variazione_pct !== null && p.variazione_pct !== undefined && (
                    <div className={`text-right ${coloreVariazione(p.variazione_pct)}`}>
                      <p className="text-lg font-bold flex items-center gap-1" data-testid="prodotto-variazione">
                        {p.variazione_pct > 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        {fmtVariazione(p.variazione_pct)}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        prima {fmtPrezzo(p.prezzo_precedente)} ({fmtDataIt(p.data_precedente)})
                      </p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2 border-t">
                  <Dato label="Prezzo minimo" valore={fmtPrezzo(p.prezzo_min)} testid="prodotto-min" />
                  <Dato label="Prezzo massimo" valore={fmtPrezzo(p.prezzo_max)} testid="prodotto-max" />
                  <Dato label="Prezzo medio" valore={fmtPrezzo(p.prezzo_medio)} />
                  <Dato label="Acquisti rilevati" valore={p.n_acquisti} />
                  <Dato label="Quantità totale" valore={fmtQta(p.qta_totale)} />
                  <Dato label="Spesa totale" valore={fmtSoldi(p.spesa_totale)} />
                </div>

                <div className="pt-2 border-t text-xs text-muted-foreground space-y-1">
                  <p>Codice fornitore: <strong>{p.codice_articolo || ND}</strong>{p.codice_tipo ? ` (${p.codice_tipo})` : ''}</p>
                  <p>Unità di misura in fattura: <strong>{p.um_originale || ND}</strong></p>
                </div>

                <div className="flex items-center justify-between gap-2 pt-2 border-t">
                  <span className="text-[11px] uppercase tracking-wide text-muted-foreground">Categoria di costo</span>
                  {isAdmin ? (
                    <CategoriaSelect
                      valore={p.categoria}
                      categorie={categorieCosto}
                      onChange={salvaCategoria}
                      testid="prodotto-categoria-select"
                      className="h-8 w-[180px]"
                    />
                  ) : (
                    <Badge variant={p.categoria ? 'secondary' : 'outline'} className="text-[10px]">
                      {p.categoria || 'DA CLASSIFICARE'}
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {serie.length > 1 && (
              <Card data-testid="prodotto-grafico">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">Andamento del prezzo</CardTitle>
                </CardHeader>
                <CardContent className="h-56 pr-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={serie} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="data" tick={{ fontSize: 10 }} />
                      <YAxis tick={{ fontSize: 10 }} domain={['auto', 'auto']} />
                      <Tooltip
                        formatter={(v) => fmtPrezzo(v)}
                        labelFormatter={(l, payload) => payload?.[0]?.payload?.completa || l}
                      />
                      <Line type="monotone" dataKey="prezzo" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            <Card data-testid="prodotto-storico">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Storico prezzi ({storico.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {storico.slice().reverse().map((s) => (
                  <div key={s.id} className="flex items-center justify-between gap-3 py-2 border-b last:border-0">
                    <div className="min-w-0">
                      <p className="text-sm font-medium">{fmtDataIt(s.data)}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        Fatt. {s.numero_documento || ND} · {fmtQta(s.quantita)} {s.um_originale || ''}
                        {s.sconto_percentuale ? ` · sconto ${s.sconto_percentuale}%` : ''}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold tabular-nums">{fmtPrezzo(s.prezzo_netto_unitario)}</p>
                      <p className="text-[11px] text-muted-foreground">riga {fmtSoldi(s.prezzo_totale)}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {dati.simili?.length > 0 && (
              <Card data-testid="prodotto-simili">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Store className="w-4 h-4 text-primary" /> Possibili alternative da altri fornitori
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Suggerimenti con la stessa unità di misura: verifica tu che siano davvero lo
                    stesso prodotto, il sistema non li considera equivalenti.
                  </p>
                  {dati.simili.map((s) => (
                    <div
                      key={s.id}
                      className="flex items-center justify-between gap-3 p-2 rounded-lg border cursor-pointer hover:border-primary/40"
                      onClick={() => navigate(`/acquisti/prodotti/${s.id}`)}
                      data-testid={`prodotto-simile-${s.id}`}
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{s.descrizione}</p>
                        <p className="text-xs text-muted-foreground truncate">{s.fornitore_nome} · {fmtDataIt(s.ultima_data)}</p>
                      </div>
                      <Badge variant="outline" className="shrink-0 tabular-nums">{fmtPrezzo(s.ultimo_prezzo)}</Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </main>
    </div>
  );
};

export default AcquistiProdottoPage;
